import { useEffect, useState } from 'react';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Loader2, Paperclip, ExternalLink } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { formatDistanceToNow, format } from 'date-fns';

interface RequestRow {
  id: string;
  company_id: string;
  requested_by: string;
  status: string;
  reason: string;
  attachment_url: string | null;
  attachment_name: string | null;
  superuser_notes: string | null;
  created_at: string;
  company: {
    id: string;
    name: string;
    suspension_reason: string | null;
  } | null;
  requester: {
    id: string;
    first_name: string | null;
    last_name: string | null;
    email: string | null;
  } | null;
}

const statusBadge = (status: string) => {
  switch (status) {
    case 'pending':
      return (
        <Badge className="bg-amber-100 text-amber-700 hover:bg-amber-100 border-amber-200">
          Pending Review
        </Badge>
      );
    case 'approved':
      return (
        <Badge className="bg-green-100 text-green-700 hover:bg-green-100 border-green-200">
          Approved
        </Badge>
      );
    case 'rejected':
      return (
        <Badge className="bg-red-100 text-red-700 hover:bg-red-100 border-red-200">
          Rejected
        </Badge>
      );
    case 'withdrawn':
      return (
        <Badge variant="secondary">Withdrawn</Badge>
      );
    default:
      return <Badge variant="outline">{status}</Badge>;
  }
};

export default function CompanyReactivationRequests() {
  const [requests, setRequests] = useState<RequestRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState<RequestRow | null>(null);
  const [actionDialog, setActionDialog] = useState<'approve' | 'reject' | null>(null);
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [attachmentSignedUrl, setAttachmentSignedUrl] = useState<string | null>(null);

  const fetchRequests = async () => {
    setLoading(true);
    try {
      const { data: requestsData, error } = await supabase
        .from('company_suspension_requests')
        .select(`
          *,
          company:companies(id, name, suspension_reason)
        `)
        .order('created_at', { ascending: false });

      if (error) throw error;

      if (!requestsData || requestsData.length === 0) {
        setRequests([]);
        return;
      }

      // Fetch requester profiles separately
      const requesterIds = [...new Set(
        requestsData.map(r => r.requested_by)
      )];

      const { data: profilesData } = await supabase
        .from('profiles')
        .select('id, first_name, last_name, email')
        .in('id', requesterIds);

      const profileMap = (profilesData || []).reduce(
        (acc, p) => ({ ...acc, [p.id]: p }),
        {} as Record<string, any>
      );

      const enriched = requestsData.map(r => ({
        ...r,
        requester: profileMap[r.requested_by] || null
      }));

      setRequests(enriched as any);
      return;
    } catch (e: any) {
      console.error('[CompanyReactivationRequests] fetch error', e);
      toast.error(e?.message || 'Failed to load requests');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRequests();
  }, []);

  const requesterName = (r: RequestRow) => {
    const first = r.requester?.first_name || '';
    const last = r.requester?.last_name || '';
    const full = `${first} ${last}`.trim();
    return full || r.requester?.email || 'Unknown';
  };

  const openReview = async (r: RequestRow) => {
    setSelected(r);
    setNotes('');
    setAttachmentSignedUrl(null);

    if (r.attachment_url) {
      try {
        const urlParts = r.attachment_url.split(
          '/storage/v1/object/public/reactivation-attachments/'
        );
        const storagePath = urlParts[1] || r.attachment_url;

        const { data, error } = await supabase.storage
          .from('reactivation-attachments')
          .createSignedUrl(storagePath, 3600);

        if (!error && data?.signedUrl) {
          setAttachmentSignedUrl(data.signedUrl);
        }
      } catch (e) {
        console.error('Failed to generate signed URL:', e);
      }
    }
  };

  const closeReview = () => {
    setSelected(null);
    setActionDialog(null);
    setNotes('');
    setAttachmentSignedUrl(null);
  };

  const startApprove = () => {
    setActionDialog('approve');
    setNotes('');
  };

  const startReject = () => {
    setActionDialog('reject');
    setNotes('');
  };

  const confirmAction = async () => {
    if (!selected || !actionDialog) return;
    if (actionDialog === 'reject' && !notes.trim()) {
      toast.error('Please provide a reason for rejection');
      return;
    }

    setSubmitting(true);
    try {
      const rpcName =
        actionDialog === 'approve'
          ? 'approve_company_reactivation_request'
          : 'reject_company_reactivation_request';

      const { error } = await supabase.rpc(rpcName as any, {
        p_request_id: selected.id,
        p_notes: notes || null,
      } as any);

      if (error) throw error;
      toast.success(
        actionDialog === 'approve'
          ? 'Request approved'
          : 'Request rejected'
      );

      try {
        const requesterEmail = selected.requester?.email;
        const requesterName = [
          selected.requester?.first_name,
          selected.requester?.last_name
        ].filter(Boolean).join(' ') || requesterEmail || 'Company Admin';
        const companyName = selected.company?.name || 'your company';

        if (requesterEmail) {
          await supabase.functions.invoke(
            'send-company-reactivation-decision',
            {
              body: {
                to_email: requesterEmail,
                to_name: requesterName,
                company_name: companyName,
                decision: actionDialog === 'approve' ? 'approved' : 'rejected',
                notes: notes || null
              }
            }
          );
        }
      } catch (emailErr) {
        // Email failure should not block the approval flow
        console.error('Failed to send decision email:', emailErr);
      }

      closeReview();
      await fetchRequests();
    } catch (e: any) {
      console.error('[CompanyReactivationRequests] action error', e);
      toast.error(e?.message || 'Action failed');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Company Reactivation Requests</CardTitle>
        <CardDescription>
          Review reactivation requests submitted by suspended company admins
        </CardDescription>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="space-y-2">
            {Array.from({ length: 4 }).map((_, i) => (
              <Skeleton key={i} className="h-12 w-full" />
            ))}
          </div>
        ) : requests.length === 0 ? (
          <div className="text-center py-12 text-muted-foreground">
            No reactivation requests
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Company</TableHead>
                <TableHead>Requested By</TableHead>
                <TableHead>Submitted</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {requests.map((r) => (
                <TableRow key={r.id}>
                  <TableCell className="font-medium">
                    {r.company?.name || '—'}
                  </TableCell>
                  <TableCell>
                    <div>
                      <div>{requesterName(r)}</div>
                      {r.requester?.email && (
                        <div className="text-xs text-muted-foreground">
                          {r.requester.email}
                        </div>
                      )}
                    </div>
                  </TableCell>
                  <TableCell>
                    <span title={format(new Date(r.created_at), 'PPpp')}>
                      {formatDistanceToNow(new Date(r.created_at), { addSuffix: true })}
                    </span>
                  </TableCell>
                  <TableCell>{statusBadge(r.status)}</TableCell>
                  <TableCell className="text-right">
                    {r.status === 'pending' && (
                      <Button size="sm" variant="outline" onClick={() => openReview(r)}>
                        Review
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent>

      {/* Review dialog */}
      <Dialog open={!!selected && !actionDialog} onOpenChange={(open) => { if (!open) closeReview(); }}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Review Reactivation Request</DialogTitle>
            <DialogDescription>
              Review the request details before approving or rejecting.
            </DialogDescription>
          </DialogHeader>

          {selected && (
            <div className="space-y-4 text-sm">
              <div>
                <p className="font-medium">Company</p>
                <p className="text-muted-foreground">{selected.company?.name}</p>
                {selected.company?.suspension_reason && (
                  <p className="text-xs text-muted-foreground mt-1">
                    Suspension reason: {selected.company.suspension_reason}
                  </p>
                )}
              </div>

              <div>
                <p className="font-medium">Requested by</p>
                <p className="text-muted-foreground">
                  {requesterName(selected)}
                  {selected.requester?.email && ` · ${selected.requester.email}`}
                </p>
              </div>

              <div>
                <p className="font-medium">Submitted</p>
                <p className="text-muted-foreground">
                  {format(new Date(selected.created_at), 'PPpp')} (
                  {formatDistanceToNow(new Date(selected.created_at), { addSuffix: true })})
                </p>
              </div>

              <div>
                <p className="font-medium">Reason / message</p>
                <div className="mt-1 p-3 bg-muted rounded-md whitespace-pre-wrap">
                  {selected.reason}
                </div>
              </div>

              {selected.attachment_url && (
                <div>
                  <p className="font-medium">Attachment</p>
                  {attachmentSignedUrl ? (
                    <a
                      href={attachmentSignedUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1 text-primary hover:underline mt-1"
                    >
                      <Paperclip className="h-4 w-4" />
                      {selected.attachment_name || 'View attachment'}
                      <ExternalLink className="h-3 w-3" />
                    </a>
                  ) : (
                    <p className="text-xs text-muted-foreground mt-1">
                      {selected.attachment_name || 'Attachment'} — generating link...
                    </p>
                  )}
                </div>
              )}
            </div>
          )}

          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={closeReview}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={startReject}
            >
              Reject
            </Button>
            <Button onClick={startApprove}>
              Approve
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Approve / Reject confirmation dialog */}
      <Dialog open={!!actionDialog} onOpenChange={(open) => { if (!open) setActionDialog(null); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {actionDialog === 'approve' ? 'Approve Request' : 'Reject Request'}
            </DialogTitle>
            <DialogDescription>
              {actionDialog === 'approve'
                ? 'Approving will reactivate the company. You can include optional notes.'
                : 'Rejecting will notify the requester. Please explain why.'}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-2">
            <Label htmlFor="action-notes">
              Notes {actionDialog === 'reject' && <span className="text-red-600">*</span>}
            </Label>
            <Textarea
              id="action-notes"
              rows={4}
              placeholder={
                actionDialog === 'approve'
                  ? 'Optional message to the company admin…'
                  : 'Reason for rejection (required)…'
              }
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
            />
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setActionDialog(null)} disabled={submitting}>
              Cancel
            </Button>
            <Button
              variant={actionDialog === 'reject' ? 'destructive' : 'default'}
              onClick={confirmAction}
              disabled={submitting || (actionDialog === 'reject' && !notes.trim())}
            >
              {submitting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Confirm {actionDialog === 'approve' ? 'Approval' : 'Rejection'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}
